import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {GtmService} from 'src/app/services/gtm.service';
import {EventActionEnum, EventCategoryEnum} from 'src/app/enums/GaTrackingEnums';
import {GtmEventEnum} from 'src/app/enums/GoogleEnums';
import {ToolsService} from '../../../../services/tools.service';
import {SearchSuggestItem} from '../../../../types/SearchSuggestItem';
import {SearchWidgetManagerService} from '../../../../services/search-widget/search-widget-manager.service';
import {SearchWidgetThemeEnum} from '../../enums/search-widget-theme.enum';
import {PlacesFreeSearchEnterHit} from '../../typings/places-free-search-enter-hit';
import {WindowSizeService} from '../../../../services/window-size.service';
import {SearchService} from '../../../../services/search/search.service';
import {DLPageTypeEnum} from '../../../../enums/DataLayerEnums';

enum WidgetCssClassNameEnum {
  TOP = 'scroll-top',
  NORMAL = 'normal',
}

@Component({
  selector: 'app-search-widget-v2-desktop',
  templateUrl: './search-widget-v2-desktop.component.html',
  styleUrls: ['./search-widget-v2-desktop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchWidgetV2DesktopComponent implements OnInit {
  isOnTop = input<boolean>();
  theme = input<SearchWidgetThemeEnum>(SearchWidgetThemeEnum.DEFAULT);
  mainCareProviderTypeId = input<number>();
  inferredCareProviderTypeAreaId = input<number>();
  inferredCategoryTypeId = input<number>();
  pageType = input<DLPageTypeEnum>();
  customCssClass = input<string>();

  public panelHeight: number = 0;
  public widgetCssClassName = computed<string>(() => {
    let cssClass: string =
      !this.tools.isNil(this.theme()) && this.theme() !== SearchWidgetThemeEnum.DEFAULT
        ? this.theme()
        : this.isOnTop()
          ? WidgetCssClassNameEnum.TOP
          : WidgetCssClassNameEnum.NORMAL;

    cssClass += !this.tools.isNil(this.customCssClass()) ? ` ${this.customCssClass()}` : '';
    return cssClass;
  });
  protected readonly SearchWidgetThemeEnum = SearchWidgetThemeEnum;
  @ViewChild('placesInput') protected placesInput;
  private tabletTreshold: number = 992;
  private windowSizeService = inject(WindowSizeService);
  private size = this.windowSizeService.windowSize;
  public isTablet = computed<boolean>(() => this.size() && this.size()[0] < this.tabletTreshold);
  private searchService = inject(SearchService);
  public fixedWidget = this.searchService.fixedWidget;
  public megaMenuDesktopOpen = this.searchService.megaMenuDesktopOpen;
  private state = this.searchService.searchWidgetState;
  public isStateLoaded = computed(() => this.state() !== null);
  public isSubmitButtonActive = computed(
    () =>
      this.state() &&
      (this.state().autocompleteValueChanged || this.state().autocompletePlaceValueChanged) &&
      (this.state().autocompleteCurrentValue !== '' || this.state().autocompletePlaceCurrentValue !== ''),
  );

  constructor(
    private tools: ToolsService,
    private searchWidgetManagerService: SearchWidgetManagerService,
    private gtmService: GtmService,
    private _change: ChangeDetectorRef,
  ) {
    effect(() => {
      this.fixedWidget();
    });
  }

  public ngOnInit(): void {
    this.searchWidgetManagerService.softResetWidgetState();
    this.searchWidgetManagerService.updateValuesInSearchWidgetState();
  }

  public onFreeSearchClick(): void {
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.SEARCHNAV,
      Action: EventActionEnum.FREE_SEARCH_FIELD_CLICK,
      Label: 'click',
      Value: 1,
    };

    this.gtmService.pushTag(gtmTag);
    this._change.markForCheck();
  }

  public onFreeSearchClear(): void {
    this.searchWidgetManagerService.clearFreeSearchTerm(true);
    this._change.markForCheck();
  }

  public onPlacesFreeSearchClear(): void {
    this.searchWidgetManagerService.clearPlacesFreeSearchTerm();
    this._change.markForCheck();
  }

  public onFreeSearchOptionSelected(selectedOption: SearchSuggestItem): void {
    this.searchWidgetManagerService.registerGtmGa4Event(
      `${EventCategoryEnum.SEARCHNAV} - free search suggestion selected`,
      GtmEventEnum.CTA_CLICK,
      this.pageType(),
      this.mainCareProviderTypeId(),
      this.inferredCareProviderTypeAreaId(),
      this.inferredCategoryTypeId(),
    );

    this.searchWidgetManagerService.redirectToSearchResultsForSelectedSuggestItem(selectedOption);
    this._change.markForCheck();
  }

  public onPlacesSearchOptionSelected(selectedOption: string): void {
    this.searchWidgetManagerService.onPlacesSelection(selectedOption, this.state().autocompleteCurrentValue);
    this._change.markForCheck();
  }

  public onFreeSearchEnterHit(event: string): void {
    if (!this.tools.isNil(event) && event !== '') {
      this.searchWidgetManagerService.registerGtmGa4Event(
        `${EventCategoryEnum.SEARCHNAV} - free search enter hit`,
        GtmEventEnum.CTA_CLICK,
        this.pageType(),
        this.mainCareProviderTypeId(),
        this.inferredCareProviderTypeAreaId(),
        this.inferredCategoryTypeId(),
      );

      this.searchWidgetManagerService.freeSearchEnterHit(event, '');
      this._change.markForCheck();
    }
  }

  public onPlacesFreeSearchEnterHit(event: PlacesFreeSearchEnterHit): void {
    if (!this.tools.isNil(event)) {
      this.searchWidgetManagerService.registerGtmGa4Event(
        `${EventCategoryEnum.SEARCHNAV} - place free search enter hit`,
        GtmEventEnum.CTA_CLICK,
        this.pageType(),
        this.mainCareProviderTypeId(),
        this.inferredCareProviderTypeAreaId(),
        this.inferredCategoryTypeId(),
      );

      this.searchWidgetManagerService.freeSearchEnterHit('', event.term);
      this._change.markForCheck();
    }
  }

  public onSubmitClick(event: Event): void {
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.SEARCHNAV,
      Action: EventActionEnum.SEARCH_BUTTON_CLICK,
      Label: 'click',
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);

    this.searchWidgetManagerService.registerGtmGa4Event(
      `${EventCategoryEnum.SEARCHNAV} - search button click`,
      GtmEventEnum.CTA_CLICK,
      this.pageType(),
      this.mainCareProviderTypeId(),
      this.inferredCareProviderTypeAreaId(),
      this.inferredCategoryTypeId(),
    );

    this.searchWidgetManagerService.searchAction();
    this._change.markForCheck();
  }

  public onChangePlaceDropdownState(event: boolean): void {
    this.searchService.placePanelOpen.set(event == undefined ? !this.searchService.placePanelOpen() : event);
    this.searchService.megaMenuDesktopOpen.set(false);

    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.SEARCHNAV,
      Action: EventActionEnum.LOCATION_DROPDOWN_CLICK,
      Label: 'click',
      Value: 1,
    };

    this.gtmService.pushTag(gtmTag);
    this._change.markForCheck();
  }

  public onMegaMenuClick() {
    this.searchService.megaMenuDesktopOpen.set(!this.searchService.megaMenuDesktopOpen());
    this.searchService.placePanelOpen.set(false);

    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.SEARCHNAV,
      Action: EventActionEnum.HITTA_VARD_BUTTON_CLICK,
      Label: 'click',
      Value: 1,
    };

    this.gtmService.pushTag(gtmTag);
    this._change.markForCheck();
  }
}
